import React, { useEffect, useRef, useState } from 'react';
import { adminService, PlatformCard } from './adminService';
import { Button } from '@/components/ui/button';

// Игровые платформы — то же, что в форме игры/фильтре каталога.
const PLATFORM_OPTIONS = [
  'PS4', 'PS5', 'Xbox One', 'Xbox Series X/S', 'PC', 'Nintendo Switch',
  'Battle.Net', 'Ubisoft', 'Roblox', 'PUBG', 'Minecraft',
];

const AdminPlatformCardsPage = () => {
  const [cards, setCards] = useState<PlatformCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setCards(await adminService.fetchPlatformCards());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    try {
      setBusy(true);
      const created = await adminService.createPlatformCard({ name: '', platforms: [], isSharing: false });
      setCards(prev => [...prev, created]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось создать карточку');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить карточку?')) return;
    try {
      setBusy(true);
      await adminService.deletePlatformCard(id);
      setCards(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить');
    } finally {
      setBusy(false);
    }
  };

  const patch = (updated: PlatformCard) =>
    setCards(prev => prev.map(c => (c.id === updated.id ? updated : c)));

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= cards.length) return;
    const next = [...cards];
    [next[index], next[target]] = [next[target], next[index]];
    setCards(next);
    try {
      setBusy(true);
      await adminService.reorderPlatformCards(next.map(c => c.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось изменить порядок');
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">Карусель платформ</h1>
          <p className="mt-1 text-sm text-gray-500">
            Карточки на главной: PlayStation и шеринг открывают каталог, остальные платформы — страницу заказа по запросу.
          </p>
        </div>
        <Button
          type="button"
          onClick={handleCreate}
          disabled={busy}
          className="rounded-xl bg-gray-900 px-6 text-white hover:bg-gray-800 disabled:opacity-40"
        >
          + Добавить карточку
        </Button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-400 shadow-sm">Загрузка…</div>
      ) : cards.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
          Карточек пока нет. Нажми «Добавить карточку».
        </div>
      ) : (
        <div className="grid gap-4">
          {cards.map((c, i) => (
            <PlatformCardRow
              key={c.id}
              card={c}
              first={i === 0}
              last={i === cards.length - 1}
              busy={busy}
              setBusy={setBusy}
              onError={setError}
              onPatch={patch}
              onDelete={handleDelete}
              onUp={() => move(i, -1)}
              onDown={() => move(i, 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PlatformCardRow: React.FC<{
  card: PlatformCard;
  first: boolean;
  last: boolean;
  busy: boolean;
  setBusy: (v: boolean) => void;
  onError: (v: string | null) => void;
  onPatch: (c: PlatformCard) => void;
  onDelete: (id: number) => void;
  onUp: () => void;
  onDown: () => void;
}> = ({ card, first, last, busy, setBusy, onError, onPatch, onDelete, onUp, onDown }) => {
  const [name, setName] = useState(card.name);
  const [platforms, setPlatforms] = useState<string[]>(card.platforms ?? []);
  const [isSharing, setIsSharing] = useState(card.isSharing);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(card.name);
    setPlatforms(card.platforms ?? []);
    setIsSharing(card.isSharing);
  }, [card.name, card.platforms, card.isSharing]);

  const samePlatforms =
    platforms.length === (card.platforms ?? []).length &&
    platforms.every(p => (card.platforms ?? []).includes(p));
  const dirty = name !== card.name || !samePlatforms || isSharing !== card.isSharing;

  const togglePlatform = (p: string) =>
    setPlatforms(prev => (prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]));

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setBusy(true);
      onError(null);
      const updated = await adminService.uploadPlatformCardImage(card.id, file);
      onPatch(updated);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Не удалось загрузить картинку');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      setBusy(true);
      onError(null);
      const updated = await adminService.updatePlatformCard(card.id, { name: name.trim(), platforms, isSharing });
      onPatch(updated);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:flex-row">
      <div className="flex flex-row items-center gap-1 sm:flex-col">
        <Button type="button" variant="outline" onClick={onUp} disabled={busy || first}
          className="h-8 w-8 rounded-lg border border-gray-200 p-0 text-gray-600 disabled:opacity-30">↑</Button>
        <Button type="button" variant="outline" onClick={onDown} disabled={busy || last}
          className="h-8 w-8 rounded-lg border border-gray-200 p-0 text-gray-600 disabled:opacity-30">↓</Button>
      </div>

      <div className="w-full shrink-0 sm:w-48">
        <div className="aspect-square overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
          {card.imageUrl ? (
            <img src={card.imageUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">Нет картинки</div>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}
          className="mt-2 w-full rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50">
          {card.imageUrl ? 'Заменить картинку' : 'Загрузить картинку'}
        </Button>
      </div>

      <div className="flex-1 space-y-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">Название</span>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Напр. PlayStation 5"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-400 focus:bg-white"
          />
        </label>

        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={isSharing} onChange={e => setIsSharing(e.target.checked)} />
          Раздел Share
        </label>

        {!isSharing && <div>
          <span className="mb-1 block text-sm font-medium text-gray-700">Платформы (фильтр каталога)</span>
          <div className="flex flex-wrap gap-2">
            {PLATFORM_OPTIONS.map(p => {
              const active = platforms.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => togglePlatform(p)}
                  className={`rounded-full border px-3 py-1.5 text-sm transition ${
                    active
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </div>}

        <div className="flex items-center gap-2">
          <Button type="button" onClick={handleSave} disabled={busy || !dirty}
            className="rounded-xl bg-gray-900 px-4 text-white hover:bg-gray-800 disabled:opacity-30">Сохранить</Button>
          <Button type="button" variant="outline" onClick={() => onDelete(card.id)} disabled={busy}
            className="rounded-xl border border-red-200 px-4 text-red-600 hover:bg-red-50">Удалить</Button>
        </div>
      </div>
    </div>
  );
};

export default AdminPlatformCardsPage;
